import {ComponentProps, createContext, ReactNode, useContext} from "react";
import {Form} from "react-bootstrap";

interface FieldState {
    invalid: boolean;
    describedBy?: string;
}

const FieldContext = createContext<FieldState>({ invalid: false });

// A labelled form field with optional help text and an error message, both tied to the control (FieldControl or
// FieldSelect) with aria-describedby, so screen readers read them when it's focused. While error is set, the control
// is marked invalid: the message says what's wrong, rather than only the red border.
export function Field({controlId, label, help, error, className, children}: {
    controlId: string, label: ReactNode, help?: ReactNode, error?: ReactNode, className?: string, children: ReactNode
}) {
    const errorId = error ? `${controlId}-error` : undefined;
    const helpId = help ? `${controlId}-help` : undefined;
    const describedBy = [errorId, helpId].filter(Boolean).join(' ') || undefined;
    return (
        <Form.Group className={className} controlId={controlId}>
            <Form.Label>{label}</Form.Label>
            <FieldContext.Provider value={{ invalid: !!error, describedBy }}>{children}</FieldContext.Provider>
            {error && <Form.Control.Feedback type="invalid" id={errorId} className="d-block">{error}</Form.Control.Feedback>}
            {help && <Form.Text id={helpId}>{help}</Form.Text>}
        </Form.Group>
    );
}

// The control's validity and descriptions, keeping any aria-describedby of its own (e.g. a note outside the field).
function useFieldAria(ownDescribedBy?: string) {
    const {invalid, describedBy} = useContext(FieldContext);
    return {
        isInvalid: invalid,
        'aria-invalid': invalid || undefined,
        'aria-describedby': [describedBy, ownDescribedBy].filter(Boolean).join(' ') || undefined
    };
}

export function FieldControl(props: ComponentProps<typeof Form.Control>) {
    return <Form.Control {...props} {...useFieldAria(props['aria-describedby'])}/>;
}

export function FieldSelect(props: ComponentProps<typeof Form.Select>) {
    return <Form.Select {...props} {...useFieldAria(props['aria-describedby'])}/>;
}
